import { MyExpRoute } from "apps/myExperience/routes/types";
import Icon from "assets/icons";
import classNames from "classnames";
import React from "react";
import { useSearchParams } from "react-router-dom";
import Button from "shared/components/button";
import UserAgentUtil from "shared/utils/useAgent";
import { PurchaseOrderTable } from "shared/components/purchaseOrder";
import { useRegDetail } from "shared/context/regDetail";
import ButtonType from "shared/enums/ButtonTypes";
import { AutoLoginOnPurchaseInventoryTypes } from "shared/enums/InventoryType";
import Variant from "shared/enums/Variant";
import { useCopies } from "shared/hooks/useCopies";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import { redirectToStripeIntermediatePage } from "../util";
import { StripeRedirectHandlerProps } from "./type";
import { TRUE_STRING } from "shared/utils/constants";
import QueryParamKey from "shared/enums/QueryParamKey";

export default function MyExperienceStripeRedirect({
  isStripeRedirectStatusSuccess,
  makePayment,
  makePaymentError,
  displayLoader,
  makePaymentResponse
}: StripeRedirectHandlerProps) {
  const copies = useCopies();
  const { helpdeskEmail } = useRegDetail();
  const [searchParams] = useSearchParams();
  const { navigate } = useNavigateWrapper();
  const isExternalInventoryPurchase = React.useRef(
    searchParams.get(QueryParamKey.IS_EXTERNAL_INV_PURCHASE) === TRUE_STRING
  );

  const redirectToExternalInventoryPurchase = React.useCallback(() => {
    if (!isExternalInventoryPurchase.current) return;
    const paramsForExternalPurchase = new URLSearchParams();
    paramsForExternalPurchase.append(
      QueryParamKey.INVENTORY_TYPE,
      searchParams.get(QueryParamKey.INVENTORY_TYPE) as string
    );
    paramsForExternalPurchase.append(
      QueryParamKey.INVENTORY_ID,
      searchParams.get(QueryParamKey.INVENTORY_ID) as string
    );
    paramsForExternalPurchase.append(
      QueryParamKey.REG_PARTICIPATION_ID,
      searchParams.get(QueryParamKey.REG_PARTICIPATION_ID) as string
    );
    paramsForExternalPurchase.append(QueryParamKey.WITH_STATUS, TRUE_STRING);
    navigate(
      {
        pathname: MyExpRoute.EXTERNAL_INVENTORY_PURCHASE.path,
        search: paramsForExternalPurchase.toString()
      },
      { replace: true }
    );
  }, [navigate, searchParams]);

  React.useEffect(() => {
    if (isStripeRedirectStatusSuccess) {
      // if stripe payment was success then call api method directly
      redirectToStripeIntermediatePage(searchParams, makePayment, () => redirectToExternalInventoryPurchase());
    }
  }, [isStripeRedirectStatusSuccess, makePayment, navigate, redirectToExternalInventoryPurchase, searchParams]);

  const goToDashboard = React.useCallback(() => {
    if (
      makePaymentResponse?.purchaseOrder.lines.length &&
      AutoLoginOnPurchaseInventoryTypes.includes(makePaymentResponse?.purchaseOrder.lines[0].lineType)
    ) {
      // do re-login only if non-attendee purchases a ticket
      //      since old session will get expired on purchasing the ticket
      window.location.href = makePaymentResponse.myExperienceLoginUrl;
    } else {
      // for other cases, we don't need to login as the session can be continued
      navigate(MyExpRoute.HOME.path);
    }
  }, [makePaymentResponse, navigate]);

  return (
    <React.Fragment>
      <section className="container">
        <div className="purchaseConfirm stripeRedirect">
          <div className="purchaseConfirm_wrapper">
            <div
              className={classNames("purchaseConfirm_icon", {
                stripeFailedMsg: !isStripeRedirectStatusSuccess
              })}>
              {isStripeRedirectStatusSuccess ? <Icon.CheckMarkOutlineSlim /> : <Icon.ExclamationOutlineSlim />}
            </div>
            <div className="purchaseConfirm_msgWrapper">
              <div className="purchaseConfirm_msgHeading">
                <h3 className="purchaseConfirm_msgHeading--title">
                  {isStripeRedirectStatusSuccess
                    ? copies.get("stripe.redirect.payment.successful")
                    : copies.get("stripe.redirect.payment.failed.status")}
                </h3>
              </div>
              <div className="purchaseConfirm_msgDetails">
                {isStripeRedirectStatusSuccess ? (
                  displayLoader ? (
                    <React.Fragment>
                      <p className="purchaseConfirm_msgDetails--text">
                        {copies.get("stripe.redirect.fetching.registration.details")}
                      </p>
                      <div className="purchaseConfirm_msgDetails--loader">
                        <Icon.LoaderCirculer className="loaderIcon" />
                      </div>
                      <p
                        className="purchaseConfirm_msgDetails--text"
                        dangerouslySetInnerHTML={{
                          __html: copies.get("email.support.with.questions", {
                            0: `<a href="mailto:${helpdeskEmail}">${helpdeskEmail}</a>`
                          })
                        }}
                      />
                    </React.Fragment>
                  ) : makePaymentError ? (
                    <React.Fragment>
                      <p className="purchaseConfirm_msgDetails--text error">
                        {isExternalInventoryPurchase.current
                          ? copies.get("stripe.redirect.fetching.registration.details.failed")
                          : copies.get("stripe.redirect.fetching.myexp.details.failed")}
                      </p>
                      <div className="purchaseConfirm_btnWrapper">
                        <Button
                          variant={Variant.PRIMARY}
                          label={copies.get("retry")}
                          isOutline
                          type={ButtonType.BUTTON}
                          onClick={() =>
                            redirectToStripeIntermediatePage(searchParams, makePayment, () =>
                              redirectToExternalInventoryPurchase()
                            )
                          }
                        />
                      </div>
                      <p
                        className="purchaseConfirm_msgDetails--text"
                        dangerouslySetInnerHTML={{
                          __html: copies.get("email.support.with.questions", {
                            0: `<a href="mailto:${helpdeskEmail}">${helpdeskEmail}</a>`
                          })
                        }}
                      />
                    </React.Fragment>
                  ) : makePaymentResponse ? (
                    <React.Fragment>
                      <p className="purchaseConfirm_msgDetails--text">
                        {copies.get("stripe.redirect.payment.successful.msg.myexp")}
                      </p>
                      <PurchaseOrderTable
                        purchaseOrder={makePaymentResponse.purchaseOrder}
                        isMobile={isExternalInventoryPurchase.current && UserAgentUtil.isMobileDevice()}
                      />
                    </React.Fragment>
                  ) : null
                ) : (
                  <React.Fragment>
                    <p className="purchaseConfirm_msgDetails--text">
                      {copies.get("stripe.redirect.payment.failed.msg.myexp")}
                    </p>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>

          <div className="purchaseConfirm_btnWrapper">
            {!displayLoader && !isExternalInventoryPurchase.current && (
              <Button onClick={goToDashboard} label={copies.get("go.to.dashboard")} icon={<Icon.DashboardThick />} />
            )}
            {isExternalInventoryPurchase.current && !isStripeRedirectStatusSuccess && (
              <Button
                isOutline
                icon={<Icon.Refresh />}
                variant={Variant.PRIMARY}
                label={copies.get("retry")}
                onClick={redirectToExternalInventoryPurchase}
              />
            )}
          </div>
        </div>
      </section>
    </React.Fragment>
  );
}
